import { CollectDataStepType } from 'src/constants/collect-data/enums'
import { US_STATE_OPTIONS } from 'src/constants/oatfi-business/states'
import {
  AiDocumentReviewReasonCode,
  ContractorQueueFields,
  DocumentReviewTab,
  McQueueFields,
  McRejectionReason,
} from './enums'

export const documentReviewTabTitles: Record<DocumentReviewTab, string> = {
  [DocumentReviewTab.Contractor]: 'Contractor',
  [DocumentReviewTab.MasterContractor]: 'Master contractor',
}

export const contractorQueueHeaderTitles: Record<ContractorQueueFields, string> = {
  [ContractorQueueFields.driverName]: 'Contractor',
  [ContractorQueueFields.partnerName]: 'Partner',
  [ContractorQueueFields.type]: 'Document type',
  [ContractorQueueFields.finishedAt]: 'Waiting',
  [ContractorQueueFields.slpRate]: 'AI score',
}

export const mcQueueHeaderTitles: Record<McQueueFields, string> = {
  [McQueueFields.partnerId]: 'Master contractor',
  [McQueueFields.type]: 'Document type',
  [McQueueFields.status]: 'Status',
  [McQueueFields.finishedAt]: 'Waiting',
}

export const collectDataStepTitles: Record<CollectDataStepType, string> = {
  [CollectDataStepType.DriverLicense]: 'Driver license',
  [CollectDataStepType.VehicleInsurance]: 'Vehicle + insurance',
  [CollectDataStepType.EIN]: 'Business entity',
  [CollectDataStepType.InsuranceDeclaration]: 'Insurance amount declaration',
  [CollectDataStepType.VehicleRegistration]: 'Vehicle registration',
  [CollectDataStepType.Vaccination]: 'Vaccination information',
  [CollectDataStepType.BGC]: 'Background check',
  [CollectDataStepType.InsuranceBeneficiary]: 'Insurance beneficiary',
  [CollectDataStepType.SignContract]: 'Signed contract',
  [CollectDataStepType.MVR]: 'Motor vehicle record',
  [CollectDataStepType.Form]: 'Form',
  [CollectDataStepType.InsuranceCoverage]: 'Insurance',
  [CollectDataStepType.LMSTraining]: 'LMS training',
  [CollectDataStepType.MCNumber]: 'MC number',
  [CollectDataStepType.DOTNumber]: 'DOT number',
  [CollectDataStepType.PaymentMethod]: 'Payment method',
  [CollectDataStepType.PIICollection]: 'SSN & address',
}

export const mcRejectionReasonTitles: Record<McRejectionReason, string> = {
  [McRejectionReason.Expired]: 'Expired',
  [McRejectionReason.InformationMismatch]: 'Info mismatch',
  [McRejectionReason.Invalid]: 'Invalid',
  [McRejectionReason.FailedBackgroundCheck]: 'Failed background check',
  [McRejectionReason.NotOwnerOfInsurance]: 'Not listed as named insured / driver on the policy',
  [McRejectionReason.WrongCountry]: 'Wrong country',
  [McRejectionReason.DocumentUnreadable]: 'Document unreadable',
  [McRejectionReason.InformationMissing]: 'Missing information',
  [McRejectionReason.NameMissing]: 'Name not present',
  [McRejectionReason.PasswordProtected]: 'Password protected',
}

/**
 * Sentinel for the recollect modal's "Other" option. Not a reason id — the
 * `partner-review-rejection-reason` table has no such row, and Retool faking one is why its
 * payload had to special-case a null id.
 */
export const OTHER_REJECTION_REASON_VALUE = 'other'

/** File extensions the review viewer can display. Anything else renders the fallback. */
export const ACCEPTED_DOCUMENT_EXTENSIONS = ['jpg', 'jpeg', 'png', 'heic', 'pdf']

export const documentReviewPageSizeOptions = [25, 50, 100]

export const CONTRACTOR_QUEUE_GRID_COLUMNS_STORAGE_KEY =
  'admin.documentReview.contractor.gridColumns'
export const MC_QUEUE_GRID_COLUMNS_STORAGE_KEY = 'admin.documentReview.mc.gridColumns'

/**
 * Readable titles for the AI review's reason codes. Wording is Retool's `aiReasonCodesMap`; the two
 * codes Retool never mapped (`vehicle_vin_partial`, `registration_sticker_no_owner`) are written in
 * the same style. Look titles up through `resolveAiReasonTitle`, which falls back to the raw code.
 */
export const aiReasonCodeTitles: Record<AiDocumentReviewReasonCode, string> = {
  [AiDocumentReviewReasonCode.DocumentTypeMismatch]: 'Wrong document type',
  [AiDocumentReviewReasonCode.DocumentIllegible]: 'Document not legible',
  [AiDocumentReviewReasonCode.RequiredFieldsMissing]: 'Required fields missing',
  [AiDocumentReviewReasonCode.DocumentExpired]: 'Document expired',
  [AiDocumentReviewReasonCode.DocumentNotYetEffective]: 'Not effective yet',
  [AiDocumentReviewReasonCode.PolicyHolderMismatch]: 'Policy holder does not match contractor',
  [AiDocumentReviewReasonCode.NamedPartyMismatch]: 'Named party does not match contractor',
  [AiDocumentReviewReasonCode.HolderSelectionRejected]: 'Disagreement on which party matched',
  [AiDocumentReviewReasonCode.DeclaredValueMismatch]: 'Differs from what the contractor declared',
  [AiDocumentReviewReasonCode.OperatingStatusNotActive]: 'Operating status not active',
  [AiDocumentReviewReasonCode.BackgroundCheckNotClear]: 'Background check not clear',
  [AiDocumentReviewReasonCode.BackgroundCheckRecordsFound]: 'Reportable records found',
  [AiDocumentReviewReasonCode.VehicleVinMismatch]: 'VIN does not match vehicle on file',
  [AiDocumentReviewReasonCode.VehicleDetailsMismatch]: 'Vehicle details differ',
  [AiDocumentReviewReasonCode.VehicleVinPartial]: 'Only part of the VIN is visible',
  [AiDocumentReviewReasonCode.RegistrationStickerNoOwner]: 'Registration sticker names no owner',
  [AiDocumentReviewReasonCode.CrossDocumentVinMismatch]: 'VIN differs from the other document',
  [AiDocumentReviewReasonCode.PartnerRuleFailed]: 'Partner rule failed',
  [AiDocumentReviewReasonCode.PartnerRulesNotFound]: 'Partner rules could not be checked',
  [AiDocumentReviewReasonCode.LowConfidence]: 'Confidence below threshold',
  [AiDocumentReviewReasonCode.ReviewFailed]: 'AI review could not run',
}

/**
 * The temporary-license address State select: the 50 US states as two-letter codes. The shared
 * list also carries Washington DC, which the ticket (and Retool's `americanStates`) leave out.
 */
export const REVIEW_ADDRESS_STATE_OPTIONS = US_STATE_OPTIONS.filter((option) => {
  return option.value !== 'DC'
})
